import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PORTAL_TOOLTIP_Z, getTooltipPortalRoot } from "../../utils/panelZIndex";

const InfoTooltip = ({ text, title, widthClass = "w-80" }) => {
  const content = text ?? title ?? "";
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const [popupStyle, setPopupStyle] = useState({});

  useEffect(() => {
    if (!open || !triggerRef.current) return;

    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const popupWidth = 320;
      const estimatedPopupHeight = 170;
      const margin = 8;
      const gap = 6;

      // Prefer placing to the right of the icon, and flip to left if needed.
      let left = rect.right + gap;
      if (left + popupWidth + margin > window.innerWidth) {
        left = rect.left - popupWidth - gap;
      }
      left = Math.max(margin, Math.min(window.innerWidth - popupWidth - margin, left));

      // Keep top aligned near the icon while staying in viewport.
      let top = rect.top - 4;
      if (top + estimatedPopupHeight + margin > window.innerHeight) {
        top = window.innerHeight - estimatedPopupHeight - margin;
      }
      top = Math.max(margin, top);

      const nextStyle = {
        left: `${left}px`,
        top: `${top}px`,
        transform: "none",
      };
      setPopupStyle((prev) =>
        prev.left === nextStyle.left &&
        prev.top === nextStyle.top &&
        prev.transform === nextStyle.transform
          ? prev
          : nextStyle
      );
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        ref={triggerRef}
        className="inline-flex items-center justify-center w-3 h-3 rounded-full border border-gray-600 bg-transparent p-0 text-gray-300 text-xs leading-none cursor-help select-none"
        aria-label="Show help"
        aria-expanded={open}
        onPointerDown={(e) => {
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            setOpen(false);
          }
        }}
      >
        ?
      </button>
      {open &&
        createPortal(
          <div
            className={`fixed ${widthClass} p-2 rounded border border-gray-600 bg-gray-900 text-xs leading-5 text-gray-200 whitespace-pre-line shadow-lg normal-case tracking-normal font-normal pointer-events-auto`}
            style={{ ...popupStyle, zIndex: PORTAL_TOOLTIP_Z }}
          >
            {content}
          </div>,
          getTooltipPortalRoot() ?? document.body
        )}
    </span>
  );
};

export default InfoTooltip;
